/**
 * Entrar com o código enviado ao e-mail (ajuste do owner 03/11, ADR-0149). // ux-neutrality:allow doc-comment
 *
 * É o que a pessoa vê ao abrir sem sessão o endereço do caso impresso no QR do exame: informa o
 * e-mail da conta, recebe um código de 6 dígitos e entra com ele. A confirmação do envio é a mesma
 * com ou sem conta, e a recusa da entrada é uma só (código incorreto ou vencido).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Button, Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { CodigoError, entrarComCodigo, pedirCodigo } from '@/lib/auth';

const DIGITOS = 6;

export interface EntrarComCodigoProps {
  /** Recebe a sessão aberta pelo código; a página guarda e segue para o caso. */
  onEntrou: (sessao: string) => Promise<void> | void;
  onUsarSenha: () => void;
  emailInicial?: string;
}

function mensagemDoErro(err: unknown): string {
  const t = ptBR.login.codigo;
  const reason = err instanceof CodigoError ? err.reason : 'unknown';
  if (reason === 'invalid_code') return t.errorInvalid;
  if (reason === 'too_many_attempts') return t.errorTooMany;
  if (reason === 'network') return t.errorNetwork;
  return ptBR.errors.generic;
}

function Alerta({ erro }: { erro: string | null }) {
  if (!erro) return null;
  return (
    <p
      id="codigo-erro"
      role="alert"
      data-testid="codigo-erro"
      className="border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
    >
      {erro}
    </p>
  );
}

export function EntrarComCodigo({
  onEntrou,
  onUsarSenha,
  emailInicial = '',
}: EntrarComCodigoProps) {
  const apiClient = useApiClient();
  const t = ptBR.login.codigo;
  const emailRef = useRef<HTMLInputElement>(null);
  const codigoRef = useRef<HTMLInputElement>(null);

  const [etapa, setEtapa] = useState<'email' | 'codigo'>('email');
  const [email, setEmail] = useState(emailInicial);
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [entrando, setEntrando] = useState(false);
  const [espera, setEspera] = useState(0);
  const [reenviado, setReenviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    (etapa === 'email' ? emailRef : codigoRef).current?.focus();
  }, [etapa]);

  useEffect(() => {
    if (espera <= 0) return undefined;
    const relogio = window.setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => window.clearTimeout(relogio);
  }, [espera]);

  async function enviar(): Promise<boolean> {
    setErro(null);
    setEnviando(true);
    try {
      const pedido = await pedirCodigo(apiClient, email.trim());
      setEspera(pedido.reenviar_em_segundos);
      return true;
    } catch (err) {
      setErro(mensagemDoErro(err));
      return false;
    } finally {
      setEnviando(false);
    }
  }

  async function handlePedir(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    if (await enviar()) {
      setCodigo('');
      setReenviado(false);
      setEtapa('codigo');
    }
  }

  async function handleReenviar() {
    if (await enviar()) {
      setCodigo('');
      setReenviado(true);
      codigoRef.current?.focus();
    }
  }

  async function handleEntrar(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setErro(null);
    setEntrando(true);
    try {
      const resp = await entrarComCodigo(apiClient, email.trim(), codigo);
      await onEntrou(resp.access_token);
    } catch (err) {
      setErro(mensagemDoErro(err));
      setEntrando(false);
    }
  }

  const alerta = <Alerta erro={erro} />;
  const descrito = erro ? 'codigo-erro' : undefined;

  const linkDaSenha = (
    <button
      type="button"
      onClick={onUsarSenha}
      data-testid="codigo-usar-senha"
      className="self-start text-body-sm text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
    >
      {t.usarSenha}
    </button>
  );

  if (etapa === 'email') {
    return (
      <form
        onSubmit={handlePedir}
        aria-describedby={descrito}
        className="mt-[26px] flex flex-col gap-4"
        data-testid="codigo-etapa-email"
      >
        <Field label={ptBR.login.emailLabel} htmlFor="codigo-email-input">
          <Input
            ref={emailRef}
            id="codigo-email-input"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={ptBR.login.emailPlaceholder}
            disabled={enviando}
            data-testid="codigo-email"
          />
        </Field>
        {alerta}
        <Button
          type="submit"
          size="block"
          disabled={enviando}
          className="mt-1.5 tracking-[0.04em]"
          data-testid="codigo-pedir"
        >
          {enviando ? t.submitting : t.submit}
        </Button>
        {linkDaSenha}
      </form>
    );
  }

  return (
    <form
      onSubmit={handleEntrar}
      aria-describedby={descrito}
      className="mt-[26px] flex flex-col gap-4"
      data-testid="codigo-etapa-codigo"
    >
      <p role="status" data-testid="codigo-enviado" className="text-body-sm text-fg-muted">
        {reenviado ? t.reenviado : t.sentIntro(email.trim())}
      </p>
      <Field label={t.codigoLabel} htmlFor="codigo-input">
        <Input
          ref={codigoRef}
          id="codigo-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={DIGITOS}
          required
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, DIGITOS))}
          placeholder={t.codigoPlaceholder}
          disabled={entrando}
          className="font-mono text-[22px] tracking-[0.4em]"
          data-testid="codigo-digitos"
        />
      </Field>
      {alerta}
      <Button
        type="submit"
        size="block"
        disabled={entrando || codigo.length !== DIGITOS}
        className="mt-1.5 tracking-[0.04em]"
        data-testid="codigo-entrar"
      >
        {entrando ? t.entrando : t.entrar}
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-3 text-body-sm">
        <button
          type="button"
          onClick={() => void handleReenviar()}
          disabled={enviando || espera > 0}
          data-testid="codigo-reenviar"
          className="text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:text-fg-dim disabled:no-underline"
        >
          {espera > 0 ? t.reenviarEm(espera) : t.reenviar}
        </button>
        <button
          type="button"
          onClick={() => {
            setErro(null);
            setEtapa('email');
          }}
          data-testid="codigo-trocar-email"
          className="text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {t.trocarEmail}
        </button>
      </div>
      {linkDaSenha}
    </form>
  );
}
